import { type AdInputLookup, type EnrichedAdInputField, getAt } from '@finn-no/mobility-item-insertion-shared';
import { i18n } from '@lingui/core';
import { Plural, Trans } from '@lingui/react/macro';
import { Button } from '@warp-ds/react';
import { useFormContext } from 'react-hook-form';
import type { WidgetModelArray } from '../../types/widget.js';
import { focusElement } from '../utils/focus.js';

type Props = {
  adInputLookup: AdInputLookup;
  widgetModel: WidgetModelArray;
};

const flattenErrors = (errors: Record<string, any>, parent = ''): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(errors)) {
    if (typeof value === 'object' && Object.hasOwn(value, 'message')) {
      result[parent + key] = value.message;
    } else if (typeof value === 'object' && value !== null) {
      // Recursively flatten nested objects and merge into result
      const nestedErrors = flattenErrors(value, `${key}.`);
      for (const [nestedKey, nestedValue] of Object.entries(nestedErrors)) {
        result[nestedKey] = nestedValue;
      }
    }
  }

  return result;
};

export const ErrorSummary = ({ adInputLookup, widgetModel }: Props) => {
  const {
    formState: { errors },
  } = useFormContext();

  const flattenedErrors = flattenErrors(errors);
  const errorFields = Object.keys(flattenedErrors).filter((field) => !/\d\.description$/.test(field));

  const getOrderedFields = (widgets: WidgetModelArray, prefix = ''): string[] =>
    widgets.flatMap((widget) => {
      const fieldName = prefix ? `${prefix}.${widget.name}` : widget.name;
      if (widget.type === 'group' && 'children' in widget && widget.children) {
        return getOrderedFields(widget.children, fieldName);
      }
      return fieldName;
    });
  const orderedFields = getOrderedFields(widgetModel);

  const sortedErrorFields = [...errorFields].sort((a, b) => orderedFields.indexOf(a) - orderedFields.indexOf(b));

  if (errors.root?.serverError) {
    return (
      <p data-testid="summary-error" className="s-text-negative mb-16">
        {errors.root.serverError.message}
      </p>
    );
  }

  const unknownLabel = i18n.t({
    id: 'mobility-item-insertion.form.error-summary.unknown-field',
    comment:
      "In a few rare cases we can't resolve which field is erroring, in this case the string will say 'You are missing or need to correct Unknown Field'. To further let the user know that somethings not right it will also be followed by a message letting them know that they should reach out to customer support",
    message: 'Unknown field',
  });
  const uhOhMessage = i18n.t({
    id: 'mobility-item-insertion.form.error-summary.unknown-field.context',
    comment:
      'This is the follow up message letting users that they should try again, and reach out to customer support if it still fails',
    message:
      'Please refresh the page and try again. All changes will be lost. If the problem persists please reach out to customer support.',
  });

  if (sortedErrorFields.length > 0) {
    let firstLabel = getAt<EnrichedAdInputField>(
      adInputLookup,
      sortedErrorFields[0].replaceAll('.', '.children.'),
    )?.label;
    const hasLabel = !!firstLabel;

    if (!firstLabel) {
      firstLabel = unknownLabel;
    }

    return (
      <p className="mb-16" data-testid="summary-error" role="alert">
        <Plural
          id="mobility-item-insertion.form.missing-field"
          comment="Message shown when there are missing fields"
          value={sortedErrorFields.length}
          one={
            <Trans>
              You are missing or need to correct{' '}
              <Button link onClick={() => focusElement(sortedErrorFields[0])}>
                {firstLabel}
              </Button>
            </Trans>
          }
          _2={
            <Trans>
              You are missing or need to correct{' '}
              <Button link onClick={() => focusElement(sortedErrorFields[0])}>
                {firstLabel}
              </Button>{' '}
              and one other field
            </Trans>
          }
          other={
            <Trans>
              You are missing or need to correct{' '}
              <Button link onClick={() => focusElement(sortedErrorFields[0])}>
                {firstLabel}
              </Button>{' '}
              and {sortedErrorFields.length - 1} other fields
            </Trans>
          }
        />
        {!hasLabel && <p data-testid="error-unknown-fields">{uhOhMessage}</p>}
      </p>
    );
  }

  return null;
};
